// ════════════════════════════════════════════════════════════════════════
// leadsApiClient.ts — Cliente HTTP crudo del API de Leads (capa de
// Infraestructura)
// ════════════════════════════════════════════════════════════════════════
// CONCEPTO: Clean Architecture — capa de Infraestructura
// Este módulo NO conoce reglas de negocio (scoring) ni orquestación
// (construir el envío bilingüe) — eso vive en application/leads/
// submitLead.usecase.ts. Aquí solo quedan:
//   1. La URL base y los headers
//   2. Las llamadas fetch() en sí (POST /leads, GET /leads)
//   3. La normalización de errores de red/HTTP en un formato consistente
//
// Antes esta lógica vivía junto con el cálculo de scoring y la
// construcción del envío bilingüe en services/leadService.ts — separarlas
// es lo que permite testear/reemplazar cada capa de forma independiente.
// ════════════════════════════════════════════════════════════════════════

import type { LeadSubmission, LeadListItem, GetLeadsApiResponse } from './leadsApiClient.types';

// ── Configuración del API ─────────────────────────────────────────────
// import.meta.env — objeto especial de Vite que inyecta variables de
// entorno en tiempo de build. Solo variables con prefijo VITE_ son
// expuestas al cliente (seguridad: nunca exponer secretos del servidor).
const LEADS_API_URL: string = import.meta.env.VITE_LEADS_API_URL ?? 'http://localhost:5199';

// ── Headers comunes ───────────────────────────────────────────────────
const getHeaders = (): Record<string, string> => ({
  'Content-Type': 'application/json',
});

// ── Respuesta cruda del backend .NET ───────────────────────────────────
// Coincide con CreateLeadResponse (ReinventX.Application.Leads.Commands.CreateLead).
interface CreateLeadApiResponse {
  leadId: string;
  message: string;
}

// ── POST /leads — crear un lead ────────────────────────────────────────
// Recibe el envío bilingüe YA construido (ver submitLead.usecase.ts) —
// este cliente no arma el payload, solo lo transporta. Devuelve el
// status HTTP junto al resto de la respuesta porque el caso de uso lo
// necesita para armar el ApiResponse completo.
export const postLeadSubmission = async (
  submission: LeadSubmission,
): Promise<CreateLeadApiResponse & { status: number }> => {
  if (import.meta.env.DEV) {
    console.group('📡 leadsApiClient.postLeadSubmission');
    console.log('Endpoint:', `${LEADS_API_URL}/leads`);
    console.log('Headers:', getHeaders());
    console.log('Submission:', submission);
  }

  const response = await fetch(`${LEADS_API_URL}/leads`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(submission),
    signal: AbortSignal.timeout(10_000), // Timeout de 10 segundos
  });

  if (!response.ok) {
    const errorBody = await response.text();
    throw new Error(`API Error ${response.status}: ${errorBody}`);
  }

  const created = (await response.json()) as CreateLeadApiResponse;

  if (import.meta.env.DEV) {
    console.log('Created:', created);
    console.groupEnd();
  }

  return { ...created, status: response.status };
};

// ── GET /leads — panel administrativo, listado de leads ────────────────
// Requiere un JWT válido con rol "admin" (el backend lo valida vía
// [Authorize(Roles = "admin")] en GET /leads) — a diferencia del POST
// público del formulario, que no pide ninguna credencial.
export const getLeads = async (token: string): Promise<LeadListItem[]> => {
  const response = await fetch(`${LEADS_API_URL}/leads`, {
    method: 'GET',
    headers: { ...getHeaders(), Authorization: `Bearer ${token}` },
    signal: AbortSignal.timeout(10_000),
  });

  if (!response.ok) {
    const errorBody = await response.text();
    throw new Error(`API Error ${response.status}: ${errorBody}`);
  }

  const body = (await response.json()) as GetLeadsApiResponse;
  return body.leads;
};
